import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '@/features/auth';
import { LoadingSpinner, PageContainer, QueryErrorAlert, TabList, TabPanel, TabsRoot } from '@/shared/components';
import { useTemplateOwnerContext } from '../hooks/useTemplateOwnerContext';
import { canManageTemplate } from '../utils/templateOwnership';
import { DraftTab } from '../components/DraftTab';
import { TemplateDetailHeader } from '../components/TemplateDetailHeader';
import { TemplateMetadataForm } from '../components/TemplateMetadataForm';
import { TemplateSummaryRail } from '../components/TemplateSummaryRail/TemplateSummaryRail';
import { VersionHistoryPanel } from '../components/VersionHistory/VersionHistoryPanel';
import { useDocumentTemplateQuery } from '../hooks/useDocumentTemplateQuery';
import { resolveTemplateTab, templateTabPath, templateTabs, type TemplateTabId } from '../utils/templateTabs';

export function DocumentTemplateDetail() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const query = useDocumentTemplateQuery(id);
  const { hasPermission } = usePermissions();
  const { isInternal } = useTemplateOwnerContext();

  if (query.isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner size="xl" className="py-12" message="Loading template..." />
      </PageContainer>
    );
  }

  if (query.isError || !query.data) {
    return (
      <PageContainer>
        <QueryErrorAlert message="Could not load this template. It may not exist, or you may not have access." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
      </PageContainer>
    );
  }

  const template = query.data;
  const canManage = canManageTemplate(template, isInternal);
  const canEdit = canManage && hasPermission({ resource: 'document_template', action: 'edit' });
  const activeTab = resolveTemplateTab(tab, canManage);
  if (activeTab === null) {
    return <Navigate to={templateTabPath(template.id, 'details')} replace />;
  }
  const showTab = (next: TemplateTabId) => navigate(templateTabPath(template.id, next), { replace: true });

  return (
    <TabsRoot
      tabs={templateTabs(canManage)}
      activeTab={activeTab}
      onTabChange={showTab}
      idPrefix="document-template"
      ariaLabel="Template sections"
      variant="header"
    >
      <TemplateDetailHeader template={template} isInternal={isInternal} canManage={canManage} tabs={<TabList />} />
      <PageContainer>
        <TabPanel>
          <div hidden={activeTab !== 'details'}>
            <div className="grid items-start gap-6 xl:grid-cols-3">
              <div className="min-w-0 xl:col-span-2">
                <TemplateMetadataForm template={template} canEdit={canEdit} />
              </div>
              <TemplateSummaryRail
                template={template}
                onOpenDraft={canManage ? () => showTab('draft') : undefined}
              />
            </div>
          </div>
          {activeTab === 'draft' && <DraftTab template={template} canEdit={canEdit} />}
          {activeTab === 'versions' && (
            <div role="region" aria-label="Versions">
              <VersionHistoryPanel
                templateId={template.id}
                hasDraft={template.hasDraft}
                canRestore={canManage}
                onRestored={() => showTab('draft')}
                onOpenDraft={canManage ? () => showTab('draft') : undefined}
              />
            </div>
          )}
        </TabPanel>
      </PageContainer>
    </TabsRoot>
  );
}
